import axios from 'axios';
import getFullConfig from './getFullConfig';
import { getSessionURL } from './sessionURL';

const registeredGoogleChatEventNames = new Set<string>();

/**
 * Sends a `message` to a Google Chat space if one is configured to receive notifications
 * for `eventName`
 *
 * Note: The `eventName` must have been registered using `registerGoogleChatEventName` or this
 * function will throw.
 *
 * @param eventName the type of event that occurred. used to control the destination Google Chat space.
 * @param message details to send in the message to help understand the context of the message.
 */
export async function sendGoogleChatNotification(eventName: string, message: string) {
    const config = getFullConfig();

    if (!registeredGoogleChatEventNames.has(eventName)) {
        throw new Error(
            `Received unknown eventName ${eventName} in \`sendGoogleChatNotification\`. You must register event names so they can be enumerated for configuration purposes.`
        );
    }

    if (!config) {
        return;
    }

    const googleChatSpaceNames = config.config.googleChatNotificationEventConfig[eventName];

    if (!googleChatSpaceNames) {
        return;
    }

    const sessionURL = await getSessionURL();

    googleChatSpaceNames.forEach((googleChatSpaceName: string) => {
        const googleChatSpaceWebhookURL = config.config.googleChatSpaceWebhookURLs[googleChatSpaceName];

        if (!googleChatSpaceWebhookURL) {
            return;
        }

        axios.post(
            googleChatSpaceWebhookURL,
            {
                text: `-----------------------------\nEvent: ${eventName}\n\nDetails: ${message}\n\nURL: ${window.location.href}\n\nView Session: ${sessionURL}\n----------------------------------`,
            },
            {
                headers: {
                    'Content-Type': 'application/json; charset=UTF-8',
                },
            }
        );
    });
}

/**
 * Register an event name that will be used to send Google Chat notifications.
 *
 * Event names must be registered so that they can be enumerated by someone
 * maintaining the mapping between event names and Google Chat spaces.
 *
 * The `eventName` should be registered in module scope in the same module
 * that sends the notifications. Doing so makes it easy to maintain event
 * names that are still being used by the application.
 *
 * @param eventName the event name to register
 */
export function registerGoogleChatEventName(eventName: string) {
    registeredGoogleChatEventNames.add(eventName);
}

function showEventNamesIfRequested() {
    if (!window.location.search.includes('showGoogleChatEventNames')) {
        return;
    }

    setTimeout(() => {
        const names = Array.from(registeredGoogleChatEventNames).join(', ');
        // eslint-disable-next-line no-alert
        alert(`Google Chat event names: ${names}`);
    });
}

showEventNamesIfRequested();
